import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { UseGameManagerReturn, Notification, AdminPanelState, PlayerDetailsModalState, UserSettingsModalState, HomeZoneModalState } from '../types/game.types';

let notificationIdCounter = 0;

const GameManagerContext = createContext<UseGameManagerReturn | null>(null);

export function GameManagerProvider({ children }: { children: React.ReactNode }) {
  // Notifications state
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Admin panel state
  const [adminPanel, setAdminPanel] = useState<AdminPanelState>({
    isOpen: false,
  });

  // Player details modal state
  const [playerDetailsModal, setPlayerDetailsModal] = useState<PlayerDetailsModalState>({
    isOpen: false,
    playerData: null,
  });

  // User settings modal state
  const [userSettingsModal, setUserSettingsModal] = useState<UserSettingsModalState>({
    isOpen: false,
  });

  // Home zone modal state
  const [homeZoneModal, setHomeZoneModal] = useState<HomeZoneModalState>({
    isOpen: false,
    zone: null,
    zoneId: null,
  });

  // Notification management
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notification-${++notificationIdCounter}`,
      timestamp: Date.now(),
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove notification after duration (default 5 seconds)
    const duration = notification.duration || 5000;
    if (duration > 0) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
      }, duration);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Admin panel management
  const openAdminPanel = useCallback(() => {
    setAdminPanel(prev => ({ ...prev, isOpen: true }));
  }, []);

  const closeAdminPanel = useCallback(() => {
    setAdminPanel(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Player details modal management
  const showPlayerDetails = useCallback((playerData: { userId: string; username: string; role: string; isLocal: boolean }) => {
    setPlayerDetailsModal({
      isOpen: true,
      playerData,
    });
  }, []);

  const closePlayerDetails = useCallback(() => {
    setPlayerDetailsModal({
      isOpen: false,
      playerData: null,
    });
  }, []);

  // User settings modal management
  const openUserSettingsModal = useCallback(() => {
    setUserSettingsModal({
      isOpen: true,
    });
  }, []);

  const closeUserSettingsModal = useCallback(() => {
    setUserSettingsModal({
      isOpen: false,
    });
  }, []);

  // Home zone modal management
  const showZoneModal = useCallback((zoneId: string | null) => {
    setHomeZoneModal({
      isOpen: true,
      zone: null,  // Don't store zone object - look it up fresh from React state
      zoneId: zoneId,
    });
  }, []);

  const closeZoneModal = useCallback(() => {
    setHomeZoneModal({
      isOpen: false,
      zone: null,
      zoneId: null,
    });
  }, []);

  // Zone action handler for zone clicks (same as hover for now)
  const handleZoneAction = useCallback((zoneId: string | null) => {
    // For now, zone clicks trigger the same modal as hover
    // This can be enhanced later for different click vs hover behavior
    showZoneModal(zoneId);
  }, [showZoneModal]);


  // Set up bridge for Phaser to React communication via global window functions
  useEffect(() => {
    // Set up window bridges with proper error handling
    try {
      // Set up global bridge functions that Phaser can call
      (window as any).showPlayerDetails = showPlayerDetails;
      (window as any).showZoneModal = showZoneModal;
      (window as any).closeZoneModal = closeZoneModal;
      (window as any).handleZoneAction = handleZoneAction;
      
    } catch (error) {
      console.error('Error setting up bridge interface:', error);
    }
    
    // Cleanup on unmount
    return () => {
      try {
        delete (window as any).showPlayerDetails;
        delete (window as any).showZoneModal;
        delete (window as any).closeZoneModal;
        delete (window as any).handleZoneAction;
      } catch (error) {
        console.error('Error during cleanup:', error);
      }
    };
  }, [showPlayerDetails, showZoneModal, closeZoneModal, handleZoneAction]);

  const value: UseGameManagerReturn = {
    // UI state
    notifications,
    adminPanel,
    playerDetailsModal,
    userSettingsModal,
    homeZoneModal,
    addNotification,
    removeNotification,
    clearNotifications,
    openAdminPanel,
    closeAdminPanel,
    showPlayerDetails,
    closePlayerDetails,
    openUserSettingsModal,
    closeUserSettingsModal,
    showZoneModal,
    closeZoneModal,
  };

  return (
    <GameManagerContext.Provider value={value}>
      {children}
    </GameManagerContext.Provider>
  );
}

// Custom hook must be declared with function declaration for Fast Refresh compatibility
export function useGameManager(): UseGameManagerReturn {
  const context = useContext(GameManagerContext);
  if (!context) {
    throw new Error('useGameManager must be used within a GameManagerProvider');
  }
  return context;
}